import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  ArrowRight, ArrowLeft, UploadCloud, FileText, CheckCircle2, Loader2, X,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "../components/ui/select";
import { RadioGroup, RadioGroupItem } from "../components/ui/radio-group";
import {
  TURNOVER_OPTIONS, BORROWING_OPTIONS, FUNDING_OPTIONS, PURPOSE_OPTIONS, FACILITY_OPTIONS,
} from "../constants/options";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STEPS = ["Your Details", "Funding Needs", "Confirm & Submit"];

const emptyForm = {
  full_name: "", mobile: "", email: "", company_name: "", city: "",
  turnover_range: "", borrowing_range: "", funding_amount: "", purpose: "",
  facility: "", consent: false,
};

export const LeadForm = ({ presetPurpose }) => {
  const [step, setStep] = useState(0);
  const [form, setForm] = useState(emptyForm);
  const [file, setFile] = useState(null);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (presetPurpose) {
      setForm((f) => ({ ...f, purpose: presetPurpose }));
      setSubmitted(false);
    }
  }, [presetPurpose]);

  const set = (key, value) => {
    setForm((f) => ({ ...f, [key]: value }));
    setErrors((e) => ({ ...e, [key]: undefined }));
  };

  const validateStep = () => {
    const e = {};
    if (step === 0) {
      if (!form.full_name.trim()) e.full_name = "Full name is required";
      if (!/^[0-9+\-\s]{8,15}$/.test(form.mobile.trim())) e.mobile = "Enter a valid mobile number";
      if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email.trim())) e.email = "Enter a valid email address";
      if (!form.company_name.trim()) e.company_name = "Company name is required";
      if (!form.city.trim()) e.city = "City is required";
      if (!form.consent) e.consent = "Consent is required to continue";
    }
    if (step === 1) {
      if (!form.turnover_range) e.turnover_range = "Select your turnover range";
      if (!form.borrowing_range) e.borrowing_range = "Select your borrowing range";
      if (!form.funding_amount) e.funding_amount = "Select the funding amount";
      if (!form.purpose) e.purpose = "Select the purpose of funding";
      if (!form.facility) e.facility = "Select a preferred facility";
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const next = () => { if (validateStep()) setStep((s) => s + 1); };
  const back = () => setStep((s) => Math.max(0, s - 1));

  const onFileChange = (ev) => {
    const f = ev.target.files?.[0];
    if (!f) return;
    if (!f.name.toLowerCase().endsWith(".pdf")) {
      toast.error("Only PDF files are allowed.");
      return;
    }
    if (f.size > 10 * 1024 * 1024) {
      toast.error("File must be smaller than 10 MB.");
      return;
    }
    setFile(f);
  };

  const submit = async () => {
    if (!validateStep()) return;
    setSubmitting(true);
    try {
      const fd = new FormData();
      Object.entries({ ...form, consent: form.consent ? "true" : "false" }).forEach(([k, v]) => fd.append(k, v));
      if (file) fd.append("file", file);
      await axios.post(`${API}/leads`, fd);
      setSubmitted(true);
      toast.success("Your request has been received.");
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = !err.response
        ? "Could not reach the server. Please wait a few seconds and tap Submit again — your details are still here."
        : Array.isArray(detail) ? detail.map((d) => d.msg).join(" ") : (detail || "Submission failed. Please try again.");
      toast.error(msg, { duration: 6000 });
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div data-testid="form-success-screen" className="flex flex-col items-start gap-5 p-2 animate-fade-in-up">
        <div className="flex h-14 w-14 items-center justify-center rounded-full bg-globus-tealLight">
          <CheckCircle2 className="h-8 w-8 text-globus-teal" />
        </div>
        <h3 className="font-heading text-2xl sm:text-3xl tracking-tight text-globus-navy">Thank you.</h3>
        <p className="text-base leading-relaxed text-slate-600">
          Your request has been received. Our team will contact you within one working day with the preliminary assessment.
        </p>
        <p className="text-sm text-slate-500">
          This is an initial assessment only. It is not a loan sanction, financial advice, or a final CMA.
        </p>
        <Button
          data-testid="success-whatsapp-button"
          variant="outline"
          className="rounded-full border-globus-teal text-globus-teal hover:bg-globus-tealLight hover:text-globus-tealDark transition-colors duration-200"
          onClick={() => window.open(`https://wa.me/${process.env.REACT_APP_WHATSAPP_NUMBER}`, "_blank")}
        >
          Chat with us on WhatsApp
        </Button>
      </div>
    );
  }

  const err = (k) => errors[k] ? <p data-testid={`error-${k}`} className="text-xs text-red-600 mt-1">{errors[k]}</p> : null;

  return (
    <div data-testid="lead-form" className="flex flex-col gap-6">
      <div className="flex items-center gap-2" data-testid="form-progress">
        {STEPS.map((label, i) => (
          <div key={label} className="flex-1">
            <div className={`h-1.5 rounded-full transition-colors duration-300 ${i <= step ? "bg-globus-teal" : "bg-slate-200"}`} />
            <p className={`mt-2 text-[10px] sm:text-xs tracking-[0.15em] uppercase font-semibold ${i <= step ? "text-globus-teal" : "text-slate-400"}`}>
              {label}
            </p>
          </div>
        ))}
      </div>

      {step === 0 && (
        <div className="flex flex-col gap-4 animate-fade-in-up" data-testid="form-step-1">
          <div>
            <Label htmlFor="full_name">Full name</Label>
            <Input data-testid="input-full-name" id="full_name" value={form.full_name}
              onChange={(e) => set("full_name", e.target.value)} placeholder="e.g. Rajesh Sharma" className="mt-1.5" />
            {err("full_name")}
          </div>
          <div>
            <Label htmlFor="mobile">Mobile number</Label>
            <Input data-testid="input-mobile" id="mobile" inputMode="tel" value={form.mobile}
              onChange={(e) => set("mobile", e.target.value)} placeholder="e.g. 98765 43210" className="mt-1.5" />
            {err("mobile")}
          </div>
          <div>
            <Label htmlFor="email">Email address</Label>
            <Input data-testid="input-email" id="email" type="email" value={form.email}
              onChange={(e) => set("email", e.target.value)} placeholder="you@company.com" className="mt-1.5" />
            {err("email")}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="company_name">Company name</Label>
              <Input data-testid="input-company-name" id="company_name" value={form.company_name}
                onChange={(e) => set("company_name", e.target.value)} placeholder="Company Pvt. Ltd." className="mt-1.5" />
              {err("company_name")}
            </div>
            <div>
              <Label htmlFor="city">City</Label>
              <Input data-testid="input-city" id="city" value={form.city}
                onChange={(e) => set("city", e.target.value)} placeholder="e.g. Mumbai" className="mt-1.5" />
              {err("city")}
            </div>
          </div>
          <div className="rounded-md bg-slate-50 border border-slate-200 p-4">
            <div className="flex items-start gap-3">
              <Checkbox data-testid="checkbox-consent" id="consent" checked={form.consent}
                onCheckedChange={(v) => set("consent", v === true)} className="mt-0.5" />
              <Label htmlFor="consent" className="text-sm leading-relaxed text-slate-600 cursor-pointer font-normal">
                I consent to Globus Financial Services contacting me regarding my funding requirement.
              </Label>
            </div>
            {err("consent")}
          </div>
        </div>
      )}

      {step === 1 && (
        <div className="flex flex-col gap-4 animate-fade-in-up" data-testid="form-step-2">
          <div>
            <Label>Annual turnover range</Label>
            <Select value={form.turnover_range} onValueChange={(v) => set("turnover_range", v)}>
              <SelectTrigger data-testid="select-turnover" className="mt-1.5"><SelectValue placeholder="Select turnover range" /></SelectTrigger>
              <SelectContent>
                {TURNOVER_OPTIONS.map((o) => <SelectItem key={o} value={o} data-testid={`turnover-option-${o}`}>{o}</SelectItem>)}
              </SelectContent>
            </Select>
            {err("turnover_range")}
          </div>
          <div>
            <Label>Existing borrowing range</Label>
            <Select value={form.borrowing_range} onValueChange={(v) => set("borrowing_range", v)}>
              <SelectTrigger data-testid="select-borrowing" className="mt-1.5"><SelectValue placeholder="Select existing borrowing" /></SelectTrigger>
              <SelectContent>
                {BORROWING_OPTIONS.map((o) => <SelectItem key={o} value={o} data-testid={`borrowing-option-${o}`}>{o}</SelectItem>)}
              </SelectContent>
            </Select>
            {err("borrowing_range")}
          </div>
          <div>
            <Label>Funding amount required</Label>
            <Select value={form.funding_amount} onValueChange={(v) => set("funding_amount", v)}>
              <SelectTrigger data-testid="select-funding-amount" className="mt-1.5"><SelectValue placeholder="Select funding amount" /></SelectTrigger>
              <SelectContent>
                {FUNDING_OPTIONS.map((o) => <SelectItem key={o} value={o} data-testid={`funding-option-${o}`}>{o}</SelectItem>)}
              </SelectContent>
            </Select>
            {err("funding_amount")}
          </div>
          <div>
            <Label>Purpose of funding</Label>
            <Select value={form.purpose} onValueChange={(v) => set("purpose", v)}>
              <SelectTrigger data-testid="select-purpose" className="mt-1.5"><SelectValue placeholder="Select purpose" /></SelectTrigger>
              <SelectContent>
                {PURPOSE_OPTIONS.map((o) => <SelectItem key={o} value={o} data-testid={`purpose-option-${o}`}>{o}</SelectItem>)}
              </SelectContent>
            </Select>
            {err("purpose")}
          </div>
          <div>
            <Label>Preferred facility</Label>
            <RadioGroup value={form.facility} onValueChange={(v) => set("facility", v)}
              className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="radio-facility">
              {FACILITY_OPTIONS.map((o) => (
                <label key={o} data-testid={`facility-option-${o}`}
                  className={`flex cursor-pointer items-center gap-3 rounded-md border px-3 py-2.5 text-sm transition-colors duration-200 ${form.facility === o ? "border-globus-teal bg-globus-tealLight text-globus-navy" : "border-slate-300 text-slate-600 hover:border-globus-teal"}`}>
                  <RadioGroupItem value={o} id={`fac-${o}`} />
                  <span>{o}</span>
                </label>
              ))}
            </RadioGroup>
            {err("facility")}
          </div>
        </div>
      )}

      {step === 2 && (
        <div className="flex flex-col gap-5 animate-fade-in-up" data-testid="form-step-3">
          <div>
            <Label>Latest audited financial statement (PDF, optional)</Label>
            <input ref={fileInputRef} type="file" accept="application/pdf,.pdf" className="hidden"
              data-testid="input-file-hidden" onChange={onFileChange} />
            {file ? (
              <div className="mt-2 flex items-center justify-between rounded-md border border-globus-teal bg-globus-tealLight px-4 py-3" data-testid="file-selected">
                <div className="flex items-center gap-3 min-w-0">
                  <FileText className="h-5 w-5 shrink-0 text-globus-teal" />
                  <span className="truncate text-sm text-globus-navy">{file.name}</span>
                </div>
                <button type="button" data-testid="file-remove-button" onClick={() => { setFile(null); fileInputRef.current.value = ""; }}
                  className="ml-3 text-slate-500 hover:text-red-600 transition-colors duration-200" aria-label="Remove file">
                  <X className="h-4 w-4" />
                </button>
              </div>
            ) : (
              <button type="button" data-testid="file-upload-dropzone" onClick={() => fileInputRef.current.click()}
                className="mt-2 flex w-full flex-col items-center gap-2 rounded-md border-2 border-dashed border-slate-300 px-4 py-8 text-center transition-colors duration-200 hover:border-globus-teal hover:bg-globus-tealLight/40">
                <UploadCloud className="h-8 w-8 text-globus-teal" />
                <span className="text-sm font-medium text-globus-navy">Tap to upload your financial statement</span>
                <span className="text-xs text-slate-500">PDF only, up to 10 MB. Visible only to Globus admins.</span>
              </button>
            )}
          </div>
          <p className="text-xs leading-relaxed text-slate-500">
            Upload is optional. This is an initial assessment only. It is not a loan sanction, financial advice, or a final CMA.
            Final eligibility is subject to detailed review and lender norms.
          </p>
        </div>
      )}

      <div className="flex items-center justify-between gap-3 pt-1">
        {step > 0 ? (
          <Button data-testid="form-back-button" variant="ghost" onClick={back}
            className="rounded-full text-slate-600 hover:text-globus-navy hover:bg-slate-100 transition-colors duration-200">
            <ArrowLeft className="mr-2 h-4 w-4" /> Back
          </Button>
        ) : <span />}
        {step < 2 ? (
          <Button data-testid="form-next-button" onClick={next}
            className="rounded-full bg-globus-navy px-6 text-white hover:bg-globus-navyLight hover:text-white transition-colors duration-200">
            Continue <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        ) : (
          <Button data-testid="form-submit-button" onClick={submit} disabled={submitting}
            className="rounded-full bg-globus-navy px-6 text-white hover:bg-globus-navyLight hover:text-white transition-colors duration-200">
            {submitting ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Submitting...</> : <>Submit Free Check <ArrowRight className="ml-2 h-4 w-4" /></>}
          </Button>
        )}
      </div>
    </div>
  );
};
